import { Link } from 'react-router-dom';
import { useI18n } from '../i18n';
import { LanguageMenu } from '../components/LanguageMenu';
import { ThemeMenu } from '../components/ThemeMenu';
import { HomeIcon } from '../storage/icons';
import { useAuth } from '../auth';

/**
 * Coquille PUBLIQUE légère des pages de partage (`/f/:slug`, `/d/:slug`) et de dépôt
 * communautaire (`/deposit/:token`).
 *
 * Accessible DÉCONNECTÉ : en dehors de `PrivateRoute`/`AppChrome` — donc PAS de
 * sélecteur de comptes ni d'affordances authentifiées. En-tête aligné sur la coquille
 * authentifiée : la MAISON (accueil) en haut à gauche, puis langue + thème.
 * `LanguageMenu`/`ThemeMenu` ne dépendent que des providers i18n/thème (qui enveloppent
 * `<App/>` dans `main.tsx`), disponibles ici.
 *
 * Maison : `AuthProvider` enveloppe TOUTE l'app (y compris ces routes publiques) et amorce
 * la liste des comptes quelle que soit la route — donc on sait ici si un compte est logué.
 * S'il y en a AU MOINS un (`accounts`), la Maison devient un LIEN vers `/` : cliquer fait
 * repasser en mode « normal » (la zone gardée résout la session active). Sinon (visiteur
 * purement anonyme), elle reste un repère de position INACTIF — pas d'espace où revenir.
 */
export function PublicChrome({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { t } = useI18n();
  const { accounts } = useAuth();
  const loggedIn = accounts.length > 0;
  return (
    <div className="flex min-h-dvh flex-col bg-surface text-fg">
      <header className="shrink-0 border-b border-border bg-surface/95 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-5xl items-center gap-2 px-4">
          <nav aria-label={t('storage.breadcrumb')} className="min-w-0 flex-1">
            {loggedIn ? (
              // Compte logué : la Maison ramène à l'espace authentifié (mode normal).
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 rounded text-sm font-medium text-fg hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
              >
                <HomeIcon className="size-5" />
                <span className="hidden sm:inline">{t('nav.home')}</span>
              </Link>
            ) : (
              // Visiteur anonyme : repère de position INACTIF (aucune navigation publique).
              <span
                aria-current="page"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-fg"
              >
                <HomeIcon className="size-5" />
                <span className="hidden sm:inline">{t('nav.home')}</span>
              </span>
            )}
          </nav>
          <div className="flex shrink-0 items-center gap-1">
            <LanguageMenu />
            <ThemeMenu />
          </div>
        </div>
      </header>
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-8">{children}</main>
    </div>
  );
}

/**
 * Bloc d'état non-résolu (introuvable / expiré) d'une page publique : icône + titre,
 * jamais l'information par la couleur seule. Partagé par les vues publiques de partage
 * (`/d/:slug`) et d'accès communautaire (`/join`, `/deposit`).
 */
export function PublicNotice({
  title,
  icon,
}: {
  title: string;
  icon: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="flex flex-col items-center gap-3 py-12 text-center" role="alert">
      <span className="text-muted">{icon}</span>
      <p className="text-lg font-medium">{title}</p>
    </section>
  );
}
